import React, {useMemo, useRef, useState, useEffect} from "react";
import BannerImg from "../../assets/images/banner/guide_banner_7.png";
import HeroBanner from "../../components/guide/HeroBanner";
import {Heading} from "../../components/guide/Heading";
import CtaBanner from "../../components/guide/CtaBanner";
import HeaderPath from "../../components/common/HeaderPath";

// ---------- Utilities
function cx(...classes: Array<string | false | undefined>) {
    return classes.filter(Boolean).join(" ");
}

// Transition helper that animates height from 0 -> content size
function useAutoHeight(isOpen: boolean) {
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<string>("0px");

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const run = () => {
            const target = el.scrollHeight;
            setHeight(isOpen ? `${target}px` : "0px");
        };
        run();

        // Recompute on resize or content change
        const obs = new ResizeObserver(run);
        obs.observe(el);
        return () => obs.disconnect();
    }, [isOpen]);

    return {ref, height};
}

// ---------- Types
interface QAItem {
    id: string;
    q: string;
    a: React.ReactNode;
}

// ---------- Data (Provided content 그대로 반영)
const CUSTOMER = {
    title: "Customer Center",
    phone: "1668-0002",
    cta: "빠른 전화 문의하기",
    desc: "전문 컨설턴트로 구성된 분양의신은 24시간, 연중무휴 상담 받으실 수 있습니다.",
};

const FAQ: QAItem[] = [
    {
        id: "q1",
        q: "광고가 보이지 않아요. 어디서 송출되나요?",
        a: (
            <>
                <p>
                    분양의신 광고는 네이버·유튜브·구글·카카오·다음 등 국내 주요 포털과 콘텐츠 플랫폼의 모바일·PC 지면에 집행됩니다.
                </p>
                <p >
                    다만 무작위로 보이는 광고가 아니라, 하이타겟을 통해 실제 전환 가능성이 높은 고객만을 정밀 타겟팅하기 때문에 모든 사람에게
                    노출되지는 않습니다.
                </p>
                <p >
                    따라서 고객님이 직접 광고를 확인하지 못하더라도, 실제 문의와 전환 가능성이 높은 타겟층에게는 반복적으로 송출되고 있습니다.
                </p>
            </>
        ),
    },
    {
        id: "q2",
        q: "광고 성과는 어떻게 확인할 수 있나요? 보고서는 어떤 형태로 제공되나요?",
        a: (
            <>
                <p>
                    분양의신은 일일 콜보고를 통해 전환 발생 현황을 실시간 공유하고, 주간·최종 보고서로 전체 캠페인 성과를 정리해 드립니다.
                </p>
                <p >
                    보고서에는 단순 클릭수가 아니라 실제 문의 발생, 전환율, 배너·문구·게재위치별 성과 데이터까지 포함되어 있어 고객이
                    한눈에 확인할 수 있습니다. 이를 통해 광고가 어떤 메시지와 어떤 디자인이 어떤 위치에서 효과가 있었는지 까지 투명하게 확인 가능하며,
                    성과는 강화하고 불필요한 비용은 줄이는 방향으로 최적화합니다.
                </p>
            </>
        ),
    },
    {
        id: "q3",
        q: "운영 시스템은 어떻게 구성되어 있나요?",
        a: (
            <>
                <p>
                    고객님은 자료만 전달해주시면 됩니다. 이후의 기획·디자인·세팅·송출·리포트까지 분양의신이 외주없이 직접 수행합니다.
                </p>
                <p >
                    특히 현장 컨설팅 설계부터 광고 집행·최적화까지 내부 전담팀이 일괄 운영하기 때문에, 성과에 끝까지 책임지는 구조를 갖추고 있습니다.
                </p>
            </>
        ),
    },
    {
        id: "q4",
        q: "타겟팅은 어디까지 가능한가요?",
        a: (
            <>
                <p>
                    지역·연령·성별·관심사·기기뿐 아니라 소득분위까지 세분화된 타겟팅이 가능합니다. 또한 분양의신은 자체 보유한 부동산 모수 데이터를
                    결합해, 교차로 중복되는 고객군을 정밀하게 추출하고 해당 타겟에게 반복 노출을 집행합니다.
                </p>
            </>
        ),
    },
    {
        id: "q5",
        q: "배너광고만 가능한가요? LMS, 언론광고도 되나요?",
        a: (
            <>
                <p>
                    온라인 배너광고는 주요 전략 중 하나일 뿐입니다. 현장 성격과 요청에 따라 LMS(문자), 언론광고, 롤링보드, 메타광고까지 다양한
                    채널을 조합합니다. 성과 중심의 통합 미디어 믹스를 설계하여 집행하기 때문에 현장 상황에 맞는 최적 구성이 가능합니다.
                </p>
            </>
        ),
    },
    {
        id: "q6",
        q: "광고 이미지는 직접 준비해야 하나요?",
        a: (
            <>
                <p>
                    아니요. 홈페이지, 배너, 영상 등 광고 소재는 주어진 자료를 활용해 분양의신 내부 디자이너·전담팀이 직접 제작합니다. 단순 예쁜
                    디자인이 아니라, 플랫폼 AI에 선택받는 구조와 전환 중심 디자인을 연구하여 고객님께 제공합니다.
                </p>
            </>
        ),
    },
    {
        id: "q7",
        q: "운영 중 조건 변경이 있으면 수정 가능한가요?",
        a: (
            <>
                <p>
                    가능합니다. 운영 기간 중 홈페이지·배너 수정은 즉시 반영할 수 있으며, 다만 전환 데이터 기반의 학습 구조가 최적화되어 있기
                    때문에 과도한 변경은 학습 초기화로 이어져 효율이 떨어질 수 있습니다. 따라서 효율적 운영을 위해 최소화된 전략 안에서 수정이
                    진행됩니다.
                </p>
            </>
        ),
    },
    {
        id: "q8",
        q: "브랜딩 광고와 퍼포먼스 광고의 다른 점은 무엇인가요?",
        a: (
            <>
                <p>
                    브랜딩 광고는 장기적인 브랜드 이미지 형성과 인지도 제고에 초점을 맞춥니다. 한정된 타겟에게 47종 이상의 다양한 배너를 지루함 없이
                    반복 송출하여 브랜드를 각인시키고, 여러 검색광고를 동시에 운영해 공식성을 강화합니다. 또한 고퀄리티 홈페이지와 아트디자인을 통해
                    브랜드의 신뢰도와 매력도를 높여, 고객이 “믿을 수 있는 브랜드”라는 인식을 자연스럽게 형성합니다.
                </p>
                <div className={'leading-[16px]'}>
                    <br/>
                </div>
                <p>
                    반면 퍼포먼스 광고는 단기적인 성과, 즉 빠른 전환에 집중합니다. 홈페이지를 강렬하고 직관적인 구조로 설계하고, 고객이 즉시 행동하도록
                    자극적인 문구와 전환 버튼 위주로 구성합니다. 그 결과 고객에게 “언제 한번 봐야지”가 아니라, “지금 바로 문의해야겠다”라는 즉각적인
                    반응을 이끌어냅니다.
                </p>
            </>
        ),
    },
    {
        id: "q9",
        q: "전환(콜)만 KPI인가요?",
        a: (
            <>
                <p>
                    상품에 따라 다릅니다. 코어타겟 프리미엄은 콜 전환 극대화가 목표 KPI이며, 하이타겟 2.0은 도달수·브랜딩 효과를 중점으로 설계됩니다.
                    현장 상황에 맞게 상품과 목표를 설정하고 그에 최적화된 광고 구조를 집행합니다.
                </p>
            </>
        ),
    },
    {
        id: "q10",
        q: "광고비는 어떻게 책정되나요?",
        a: (
            <>
                <p>
                    광고비는 현장 상황과 목표 KPI를 분석한 뒤 맞춤형으로 책정됩니다. 고객님이 가능한 예산 범위를 제시하면, 분양의신은 그 안에서
                    성과 기여도가 낮은 매체는 정리하고, 효율이 높은 캠페인에 집중 투자하는 구조를 설계합니다.
                </p>
                <p >
                    현장과 목표에 따라 장기적으로 2~3개월 이상을 내다본 전략적 운영을 추구할 수 있고, 반대로 3~4주 안에 단기간 퍼포먼스를 집중적으로
                    내는 방식도 가능합니다. 결국 광고비는 현장 특성과 고객 상황에 따라 유연하게 달라지며, 분양의신은 그에 맞는 최적 전략을 제안합니다.
                </p>
            </>
        ),
    },
    {
        id: "q11",
        q: "현장별 광고 전략도 달라지나요?",
        a: (
            <>
                <p>
                    네. 분양의신은 모든 현장을 같은 방식으로 광고하지 않습니다. 현장의 상황, 상품의 특성, 타겟 고객에 따라 같은 현장일지라도 강조할
                    포인트와 KPI를 다르게 설정하고 그에 맞는 광고를 집행합니다. 가격을 앞세워야 하는 현장과 신뢰·브랜딩을 쌓아야 하는 현장은 전략이
                    전혀 다릅니다. 물건별 광고 전략은 [물건별 광고 가이드]을 통해 확인할 수 있습니다.
                </p>
            </>
        ),
    },
    {
        id: "q12",
        q: "영상광고도 가능한가요?",
        a: (
            <>
                <p>
                    가능합니다. 특히 유튜브로 송출되는 영상 배너는 브랜드 도달과 전환을 동시에 달성할 수 있는 강력한 수단입니다. 분양의신은 내부에서
                    직접 영상 소재를 기획·편집할 뿐 아니라, 업계 최초로 전담 아트디자인팀을 보유하고 있어 현장 특성에 맞는 고퀄리티 영상 퍼포먼스를
                    제작합니다. 단순한 영상이 아니라, 시각적 완성도와 몰입도를 극대화한 차별화된 콘텐츠를 제공합니다.
                </p>
            </>
        ),
    },
    {
        id: "q13",
        q: "본부광고와 병행하면 어떻게 되나요?",
        a: (
            <>
                <p>
                    병행은 오히려 더 큰 시너지를 만듭니다. 본부광고가 장기적인 브랜드 가치와 신뢰를 쌓아주는 역할을 한다면, 퍼포먼스 광고는 그 브랜드
                    인지도를 기반으로 현장 단위의 콜·방문·계약 같은 단기 성과를 빠르게 확보하는 데 강점이 있습니다.
                </p>
                <div className={'leading-[16px]'}>
                    <br/>
                </div>
                <p >
                    즉, 본부에서 진행하는 브랜드 광고가 ‘전장을 바꿔주는 전략적 지원군’이라면, 개인이나 팀이 진행하는 퍼포먼스 광고는 ‘그 전장에서
                    고객을 설득하고 계약으로 연결시키는 무기’입니다. 두 광고가 결합될 때, 브랜드 신뢰 → 고객 도달 → 전환 확보라는 선순환 구조가
                    완성됩니다.
                </p>
            </>
        ),
    },
    {
        id: "q14",
        q: "광고는 전국 송출이 가능한가요?",
        a: (
            <>
                <p>
                    네, 가능합니다. 다만 단순 전국 송출보다 지역별 성과를 분석해 예산을 집중 배분하는 방식이 더 효율적입니다. 분양의신은 전국 1천만
                    부동산 관심자 DB를 기반으로, 지역별로 전환 가능성이 높은 고객만 정밀 타겟팅을 추천드립니다.
                </p>
            </>
        ),
    },
    {
        id: "q15",
        q: "광고 이미지가 이쁘지 않던데  맞춰서 수정해주실 수는 없나요?",
        a: (
            <>
                <p>
                    분양의신이 추구하는 것은 단순히 예쁜 이미지가 아니라 플랫폼 AI 로직에 최적화된 전환형 디자인입니다. 따라서 임의로 변경하면 효율
                    저하가 발생할 수 있어, 성과를 유지하는 범위 안에서만 수정을 권장드립니다.
                </p>
            </>
        ),
    },
    {
        id: "q16",
        q: "최종적으로 분양의신 광고가  주는 가치는 무엇인가요? 왜 분양의신을 선택해야 하나요?",
        a: (
            <>
                <p>
                    분양의신이 추구하는 광고의 가치는 단순히 예산을 집행하는 것이 아닙니다. 우리는 브랜딩과 세일즈를 동시에 설계하여, 브랜드 신뢰도를
                    높이면서도 현장에서 실제 전환으로 이어지는 성과를 만듭니다.
                </p>
                <div className={'leading-[16px]'}>
                    <br/>
                </div>
                <p>
                    이를 가능하게 하는 기반은 업계 최초로 부동산 모수를 직접 보유하고 이식해 운영하는 하이타겟 기술입니다. 매월 180만 건 이상의 전국
                    부동산 관심자 DB와 자체 하이타겟 기술을 통해, 단순 노출이 아니라 실제 전환 가능성이 높은 고객에게 반복 노출이 이뤄집니다.
                </p>
                <div className={'leading-[16px]'}>
                    <br/>
                </div>
                <p>
                    또한 업계 최초의 전담 아트디자인팀과 최다 규모 디자이너 조직을 통해, 단순히 예쁜 디자인이 아니라 플랫폼 AI 로직에 최적화된
                    전환형·브랜딩형 디자인을 제공합니다. 여기에 6,000개 이상의 현장 경험과 기획–세팅–운영–성과 관리까지 이어지는 올인원 프로세스를
                    결합하여, 고객님이 자료만 제공하시면 광고의 모든 단계는 분양의신이 책임집니다.
                </p>
                <div className={'leading-[16px]'}>
                    <br/>
                </div>
                <p>
                    결국 분양의신 광고는 단순히 노출을 만들어내는 것이 아니라, 고객이 실제로 돈을 벌 수 있는 구조를 제공합니다. 브랜드 신뢰와 단기
                    성과가 함께 움직일 수 있도록 설계된, 업계 유일의 전략적 실행 파트너가 바로 분양의신입니다.
                </p>
            </>
        ),
    },
];

// ---------- Components
function AccordionItem({item, index, openId, setOpenId}: {item: QAItem; index: number; openId: string | null; setOpenId: (id: string | null) => void;}) {
    const isOpen = openId === item.id;
    const {ref, height} = useAutoHeight(isOpen);

    return (
        <li className="border border-[#DDDDDDDD] rounded-[6px]" key={item.id}>
            <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className={cx(
                    "w-full px-4 py-4 flex items-center gap-3 text-left sm:px-[44px] sm:py-[32px]",
                )}
                aria-expanded={isOpen}
                aria-controls={`panel-${item.id}`}
            >
                <span className={cx(
                    "mt-0.5 inline-flex w-[30px] h-[30px] shrink-0 items-center justify-center rounded-full text-[20px] font-[400] text-white",
                    isOpen ? 'bg-[#0D0D0D]' : 'bg-[#C0C0C0]'
                )}>Q</span>
                <span className="flex-1 text-[15px] sm:text-base text-slate-900 leading-6">{item.q}</span>
                <svg className={cx("mt-1 h-5 w-5 shrink-0 transition-transform duration-300", isOpen && "rotate-180")} viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" />
                </svg>
            </button>

            <div
                id={`panel-${item.id}`}
                role="region"
                aria-labelledby={`button-${item.id}`}
                style={{
                    height
                }}
                className={cx(
                    "bg-[#F5F5F5] overflow-hidden transition-[height] duration-400 ease-in-out",
                )}
            >
                <div ref={ref} className="px-4 py-4 sm:px-[44px] sm:py-[28px]">
                    <div className="flex gap-4 sm:gap-[22px]">
                          <span className={cx(
                              "mt-0.5 inline-flex w-[30px] h-[30px] shrink-0 items-center justify-center rounded-full text-[20px] font-[400] text-white bg-[#0169EB]",
                          )}>A</span>
                        <div className="leading-[1.5rem] md:leading-[33px] text-sm font-[400] text-[#0D0D0D] sm:text-[16px]">
                            {item.a}
                        </div>
                    </div>
                </div>
            </div>
        </li>
    );
}

export default function FaqPage() {
    const [openId, setOpenId] = useState<string | null>(null);

    const list = useMemo(() => FAQ, []);

    return (
        <section className="w-full bg-white">
            <div className="mx-auto w-full max-w-[1072px] px-0 sm:px-6 lg:px-0">
                <HeaderPath path={'> 자주 묻는 질문 (FAQ)'}/>
                <HeroBanner image={BannerImg} message="미분양 브랜딩부터, 완판 성공까지!"/>
                <Heading className="hidden lg:block lg:mt-[93px] lg:mb-[96px]" as="h1" size="h1">
                    자주 묻는 질문 (FAQ)
                </Heading>

                {/* Customer Center card */}
                <div className="mt-6 rounded-xl border border-slate-200 bg-[#E7F0FF] px-5 py-6 sm:mt-8 sm:px-[36px] sm:py-[38px]">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm font-[400] tracking-wide text-[#474747] sm:text-[18px]">Customer Center</p>
                            <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-[28px]">{CUSTOMER.phone}</p>
                            <p className="mt-1 text-xs text-[#5C5C5C] sm:text-[13px]">{CUSTOMER.desc}</p>
                        </div>
                        <button className="inline-flex items-center justify-center rounded-lg bg-[#0000ED] px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 active:scale-[.99] sm:px-[36px] sm:py-[12px] sm:text-[18px]">
                            {CUSTOMER.cta}
                        </button>
                    </div>
                </div>

                {/* FAQ List */}
                <ul className="mb-16 mt-12 flex flex-col gap-5 sm:mt-[93px] sm:gap-[20px] sm:mb-[93px]">
                    {list.map((item, idx) => (
                        <AccordionItem key={item.id} item={item} index={idx} openId={openId} setOpenId={setOpenId} />
                    ))}
                </ul>

                <CtaBanner
                    message="아직 해결되지 않은 질문이 있으신가요? \n전문 컨설턴트와 상담해보세요."
                    buttonText="광고문의 바로가기 →"
                    href="https://분양의신.net/contact"
                />
            </div>
        </section>
    );
}
